<script setup>
import {reactive, onMounted, onUnmounted, toRaw} from 'vue';
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {userStore} from "@/Stores/UserStore";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const compName = 'FileListComponent';

const props = defineProps({
  myProfile: Boolean,
  personId: String,
  claimId: String
});

const data = reactive({
  files: [],
  isEditing: false,
  loading: false,
  originalFileName: '',
  context: null,
  showSymbilitySelectionDialog: false,
  symbilityFilesHeaders: [
    { title: 'Name', key: 'name' },
    { title: 'Type', key: 'type' },
    { title: 'Download', key: 'id' },
  ],
  symbilityFiles: [],
  symbilityId: '',
  symbilityDownloadingFile: false
})

const markPublic = (id, status) => {
  data.loading = true;
  ApiWrapper.post("files","change-status", {id: id, status: status})
      .then((response) => {
        fetchData()
        ToastUtil.successMsg(`Marked file as ${status.toLowerCase()}`);
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false;
      })
}

const markRequired = (id) => {
  data.loading = true;
  ApiWrapper.post("files","mark-required", {id: id, personId: userStore.userInfo.id})
      .then((response) => {
        fetchData()
        ToastUtil.successMsg('Marked file as required');
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false;
      })
}

const fetchData = () => {
  data.loading = true;

  if(props.myProfile) {
    data.context = 'myprofile';
  }
  else if(props.personId) {
    data.context = 'person';
  }
  else if(props.claimId) {
    data.context = 'claims';
  }
  else {
    data.context = '';
  }

  ApiWrapper.post("files","list",  {
        myProfile: props.myProfile,
        personId: props.personId,
        claimId: props.claimId
      })
      .then((response) => {
        let viewPrivate = Permissions.checkPermissions(['files.view-private']);
        let viewAny = Permissions.checkPermissions(['files.view-any']);

        if(data.context === 'person'){
          for(let x = response.data.length - 1; x >= 0; x--){
            let curFile = response.data[x]
            if(viewAny === false){
              response.data.splice(x,1)
            }else{
              if(curFile.status !== 'Public'){
                if(viewPrivate === false){
                  response.data.splice(x, 1)
                }
              }
            }
          }
        }

        data.files = response.data;

        activeComponentStore.notifyComponents(["files"],compName);
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(() => {
        data.loading = false;
      })
}

const formatTimestampByLocale = (timestamp) => {
  const date = new Date(timestamp)
  const timeOptions = {
    hour12: true,
    hour: "numeric",
    minute: "2-digit",
  }
  const dateOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZoneName:'short'
  }
  return date.toLocaleString('en', { ...dateOptions, ...timeOptions })
}

const editFileName = (file) => {

  data.loading = true;
  ApiWrapper.post("files","edit", {
        editedItem: file
      })
      .then((response) => {
        activeComponentStore.notifyComponents(["files"],compName);
        data.loading = false;
        fetchData();
        ToastUtil.successMsg('Updated file');
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false;
      })
}

const removeFile = (file) => {
  let deleteMessage = confirm('Are you sure you want to delete this item?');
  if (deleteMessage === true) {
    
    data.loading = true;
    ApiWrapper.post("files","delete", {
          id: file.id,
          key: file.key,
        })
        .then((response) => {
          data.files = data.files.filter((f) => {
            return f.fileid !== file.fileid;
          });
          activeComponentStore.notifyComponents(['files'],compName);
          ToastUtil.successMsg('Deleted file');
          getSymbilityFilesList();
        })
        .catch((err) => {
          ToastUtil.error(err);
        })
        .finally(() => {
          data.loading = false;
        })
  }
}

const windowOpen = (file) => {
  if(Permissions.checkPermissions(['files.download'])) {
    if (file.key !== null) {
      data.loading = true;
      ApiWrapper.post("files", "download", {key: file.key})
          .then((response) => {
            window.open(response.url, "_blank");
            data.loading = false;
          })
          .catch((err) => {
            ToastUtil.error(err);
            data.loading = false;
          });
    } else {
      data.loading = true;
      ApiWrapper.post("files", "download", {filename: file.filename})
          .then((response) => {
            window.open(response.url, "_blank");
            data.loading = false;
          })
          .catch((error) => {
            ToastUtil.error(error);
            data.loading = false;
          })
    }
  }
}

const getSymbilityFilesList = () =>{
  ApiWrapper.post("claims","get-symbility-files-list",  {
    claimId: props.claimId
  })
      .then((response) => {

        data.symbilityId = response.id;
        data.symbilityFiles = response.files;
      })
      .catch((e)=>{
        if(e.status!==404){
          ToastUtil.error(e);
        }
      })
      .finally(() => {
        data.loading = false;
        removeAlreadyDownloadedSymbilityFiles()
      })
}

const downloadSymbilityFile = (fileId,fileName,fileType) => {
  data.symbilityDownloadingFile = true;

  ApiWrapper.post("claims","download-symbility-file",  {
    claimId: props.claimId,
    symbilityId: data.symbilityId,
    fileId: fileId,
    fileName: fileName,
    fileType: fileType
  })
      .then((response) => {
        if(response.status){
          if(response.status === 'success'){
            removeDownloadedSymbilityFile(fileId);
          }
        }
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(() => {
        data.symbilityDownloadingFile = false;
      })
}

const removeDownloadedSymbilityFile = (fileId) => {
  const symFiles = toRaw(data.symbilityFiles);
  data.symbilityFiles = symFiles.filter(file => file.id !== fileId)
}

const removeAlreadyDownloadedSymbilityFiles = () => {
  const files = toRaw(data.files);
  const symFiles = toRaw(data.symbilityFiles);

  data.symbilityFiles = symFiles.filter(symFile => {
    return !files.some(file=>file.filename === symFile.name)
  })
}

onMounted(()=>{
  fetchData();
  if(props.claimId){
    getSymbilityFilesList();
  }
  activeComponentStore.registerComponent(
      compName,
      [
        'files',
        'files2'
      ],
      fetchData
  );
})

onUnmounted(() => {
  activeComponentStore.deregisterComponent(compName);
});
</script>

<template>
  <v-container fluid>
    <v-card>
      <v-toolbar flat color="white">
        <v-toolbar-title>Files</v-toolbar-title>
        <v-progress-linear :active="data.loading" :indeterminate="data.loading" absolute bottom color="primary"></v-progress-linear>
        <v-btn
            v-if="Permissions.checkPermissions(['files.list'])"
            :disabled="data.loading"
            prepend-icon="mdi-refresh"
            @click="fetchData();getSymbilityFilesList();"
        ></v-btn>
        <v-divider class="mx-4" inset vertical></v-divider>
        <v-btn
            v-if="data.symbilityId && Permissions.checkPermissions(['files.list'])"
            size="small"
            variant="elevated"
            color="primary"
            prepend-icon="mdi-magnify"
            @click="data.showSymbilitySelectionDialog=true"
        >
          Symbility Files
        </v-btn>
      </v-toolbar>
<!--      </v-card-title>-->
      <v-list flat>
        <template v-for="(file, index) in data.files" :key="file.fileid">
          <v-list-item class="v-list-item--link" v-ripple>
            <template v-slot:default="{}">
              <div class="files-container-columns flex-grow">

                <div class="files-container-rows flex-grow">

                  <div class="flex-container-rows flex-grow" v-if="file.isEditing !== true && file.key !== null" @click="windowOpen(file)">
                    <v-list-item-title class="flex-grow" v-if="(file.status === 'Public' || file.status === 'Private') && data.context === 'person'">
                      {{file.status}}
                    </v-list-item-title>
                    <v-list-item-title class="flex-grow" v-if="(file.status !== 'Public' && file.status !== 'Private') && data.context === 'person'">
                      Status Not Set
                    </v-list-item-title>
                    <v-list-item-subtitle class="flex-grow" v-if="file.uploadedby !== '' && file.uploadedby !== null">
                      {{file.filename}} - {{ formatTimestampByLocale(file.created_at) }}. Uploaded by {{file.uploadedby}}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle class="flex-grow" v-else>
                      {{file.filename}} - {{ formatTimestampByLocale(file.created_at) }}
                    </v-list-item-subtitle>
                  </div>

                  <div class="flex-container-rows flex-grow" v-if="file.isEditing !== true && file.key === null" @click="windowOpen(file)">
                    <v-list-item-title class="flex-grow" v-if="(file.status === 'Public' || file.status === 'Private') && data.context === 'person'">
                      {{file.status}}
                    </v-list-item-title>
                    <v-list-item-title class="flex-grow" v-if="(file.status !== 'Public' && file.status !== 'Private') && data.context === 'person'">
                      Status Not Set
                    </v-list-item-title>
                    <v-list-item-subtitle class="flex-grow" v-if="file.uploadedby !== '' && file.uploadedby !== null">
                      {{file.title}} - {{ formatTimestampByLocale(file.created_at) }}. Uploaded by {{file.uploadedby}}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle class="flex-grow" v-else>
                      {{file.title}} - {{ formatTimestampByLocale(file.created_at) }}
                    </v-list-item-subtitle>
                  </div>

                  <div class="flex-container-rows flex-grow" v-if="file.isEditing">
                    <v-text-field v-if="file.key !== null"
                                  v-model="file.filename"
                                  label="Edit File Name"
                                  hide-details="auto"
                                  class="flex-grow"
                    ></v-text-field>
                    <v-text-field v-if="file.key === null"
                                  v-model="file.title"
                                  label="Edit File Name"
                                  hide-details="auto"
                                  class="flex-grow"
                    ></v-text-field>
                  </div>

                </div>

                <div class="files-container-columns flex-shrink pb-4">
                  <div class="files-container-columns flex-shrink" v-if="file.isEditing !== true">

                    <v-list-item-action v-if="Permissions.checkPermissions(['files.change-status'])" class="ml-2 flex-shrink">
                      <v-btn
                          class="ml-2"
                          size="small"
                          variant="elevated"
                          v-if="file.status !== 'Public' && data.context === 'person'"
                          @click="markPublic(file.id, 'Public')"
                      >
                        Mark Public
                      </v-btn>
                      <v-btn
                          class="ml-1 mr-2 flex-shrink"
                          size="small"
                          variant="elevated"
                          v-if="file.status !== 'Private' && data.context === 'person'"
                          @click="markPublic(file.id, 'Private')"
                      >
                        Mark Private
                      </v-btn>
                    </v-list-item-action>
                    <!-- <v-list-item-action v-if="Permissions.checkPermissions(['files.view-private'], userStore.userInfo.permissions)" class="mx-2">
                      <v-btn
                        class="mx-1"
                        size="small"
                        v-if="file.rdid === null && context === 'person' && Permissions.checkPermissions(['files.mark-required'])"
                        @click="markRequired(file.id)"
                      >
                        Mark Required
                      </v-btn>
                    </v-list-item-action> -->
                    <v-list-item-action class="ml-2 flex-shrink">
                      <v-btn
                          icon="mdi-pencil"
                          size="small"
                          v-if="Permissions.checkPermissions(['files.update'])"
                          @click="file.isEditing = true; data.originalFileName = file.filename"
                      >
                      </v-btn>
                    </v-list-item-action>
                    <v-list-item-action class="ml-1 mr-2 flex-shrink">
                      <v-btn
                          icon="mdi-delete"
                          size="small"
                          v-if="Permissions.checkPermissions(['files.delete'])"
                          @click.stop="removeFile(file)"
                      >
                      </v-btn>
                    </v-list-item-action>

                  </div>

                  <div class="files-container-columns flex-shrink" v-if="file.isEditing">

                    <v-list-item-action class="ml-2 flex-shrink">
                      <v-btn
                          size="x-small"
                          color="primary"
                          @click="editFileName(file); file.isEditing = false"
                      >
                        Save
                      </v-btn>
                    </v-list-item-action>
                    <v-list-item-action class="ml-1 mr-2 flex-shrink">
                      <v-btn
                          size="x-small"
                          color="error"
                          @click="file.isEditing = false; file.filename = data.originalFileName"
                      >
                        Cancel
                      </v-btn>
                    </v-list-item-action>

                  </div>
                </div>

              </div>
            </template>
          </v-list-item>
          <v-divider
              v-if="index + 1 < data.files.length"
              :key="index"
          ></v-divider>
        </template>
      </v-list>
    </v-card>
  </v-container>
  <v-dialog
    v-model="data.showSymbilitySelectionDialog"
    max-width="1000"
    width="auto"
  >
    <v-card :loading="data.symbilityDownloadingFile">
      <v-card-title>
        Symbility File Selection
      </v-card-title>
      <v-card-text>
        <v-data-table
            :items="data.symbilityFiles"
            :headers="data.symbilityFilesHeaders"
        >
          <template v-slot:[`item.name`]="{ item }" >
            {{item.name}}
          </template>
          <template v-slot:[`item.type`]="{ item }" >
            {{item.type}}
          </template>
          <template v-slot:[`item.id`]="{ item }" >
            <v-btn
                size="small"
                variant="elevated"
                color="primary"
                prepend-icon="mdi-download-box-outline"
                @click="downloadSymbilityFile(item.id,item.name,item.type)"
            >Download</v-btn>
          </template>
        </v-data-table>
        <v-alert
            icon="mdi-information-outline"
            color="info">
          Downloaded files may take a moment to appear in the Files list. Try refreshing if you don't see downloaded Symbility files right away.
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-btn
            size="small"
            variant="elevated"
            color="error"
            prepend-icon="mdi-close"
            :disabled="data.symbilityDownloadingFile"
            @click="data.showSymbilitySelectionDialog=false; fetchData();"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>

  </v-dialog>
</template>

<style scoped>
.container {
  padding: 5px;
}
</style>